/**
 * @fileoverview Component for confirming the deletion of an external ID.
 * This component includes a confirmation message and a button for confirming the deletion.
 */

import { XMarkIcon } from '@heroicons/react/24/solid'
import { useModal } from '../../../hooks/useModal'
import { DangerButton } from '../../elements/DangerButton'
import { RegularButton } from '../../elements/RegularButton'

/**
 * @interface ConfirmNewTestProps
 * @description Props for the ConfirmDeleteExternalID component.
 * @property {function} onConfirm - The function to call when deletion is confirmed. Returns a Promise.
 */

interface ConfirmNewTestProps {
    onConfirm: () => Promise<unknown>
}

/**
 * @function ConfirmDeleteExternalID
 * @description Component for confirming the deletion of an external ID.
 * @param {ConfirmDeleteExternalIDProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the confirmation message and button.
 */
export function ConfirmDeleteExternalID({ onConfirm }: ConfirmNewTestProps) {
    const [, close] = useModal()
    return (
        <div className="w-[26rem]">
            <div className="flex justify-between gap-4 mb-6">
                <div className="flex">
                    <h2 className="block font-semibold text-xl text-gray-900">
                        Delete this external ID?
                    </h2>
                </div>
                <button className="flex shrink-0 w-6 h-6" onClick={close}>
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </div>
            <div>
                <p className="text-sm text-gray-600">
                    This might break the link between the participant record in the Novoic Dashboard
                    and the participant record in the third-party system where the corresponding
                    external ID is used.
                </p>
            </div>

            <section className="flex flex-row gap-2 justify-end mt-8">
                <RegularButton onClick={close}>No, Cancel</RegularButton>
                <DangerButton
                    onClick={async () => {
                        await onConfirm()
                        close()
                    }}
                >
                    Yes, Delete
                </DangerButton>
            </section>
        </div>
    )
}
